// @eidollon/ui-filters/url
//
// Canonical FilterState ↔ URL serialization. Every entity browser uses this.
// Round-trip property tested in url.spec.ts.
//
// Encoding strategy:
//   * JSON-stringify the state.
//   * If short (< 1500 chars after JSON), base64url-encode and put in `?f=`.
//   * If long, the API exposes /v1/search/ephemeral-query (24h TTL) which
//     accepts the full payload and returns a `query_id`; the URL stores
//     `?fid=<query_id>`. This module returns a sentinel ("__SPILL__") for the
//     consumer to handle the spill — that keeps this module pure (no fetch).

import { filters as f } from '@eidollon/contracts';

export const F_PARAM = 'f'; // base64url(JSON(FilterState))
export const FID_PARAM = 'fid'; // server-side ephemeral query id (when state too large)
export const SPILL_THRESHOLD = 1500;

export const SPILL_SENTINEL = '__SPILL__' as const;
export type EncodeResult =
  | { kind: 'inline'; param: typeof F_PARAM; value: string }
  | { kind: 'spill'; payload: f.FilterState };

/** Encode a FilterState for URL embedding. Returns either an inline param
 *  string or a spill payload that the caller must POST to obtain a `fid`. */
export function encode(state: f.FilterState): EncodeResult {
  const json = stableStringify(state);
  if (json === '{}') {
    return { kind: 'inline', param: F_PARAM, value: '' };
  }
  const b64 = b64urlEncode(json);
  if (b64.length > SPILL_THRESHOLD) {
    return { kind: 'spill', payload: state };
  }
  return { kind: 'inline', param: F_PARAM, value: b64 };
}

/** Decode a URL `f` value back into a FilterState. Throws on invalid input
 *  so the caller can surface a clear error to the user instead of silently
 *  rendering an empty filter. */
export function decode(value: string): f.FilterState {
  if (!value) return {};
  let json: string;
  try {
    json = b64urlDecode(value);
  } catch {
    throw new Error('filters URL parameter is not valid base64url');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('filters URL parameter is not valid JSON');
  }
  // Zod-validate; never trust URL input.
  const result = f.FilterState.safeParse(parsed);
  if (!result.success) {
    throw new Error(`filters URL parameter failed validation: ${result.error.message}`);
  }
  return result.data;
}

/** Apply a state to a URLSearchParams, replacing prior `f`/`fid` cleanly. */
export function applyToParams(
  params: URLSearchParams,
  state: f.FilterState,
  spillId: string | null = null,
): URLSearchParams {
  params.delete(F_PARAM);
  params.delete(FID_PARAM);
  if (spillId) {
    params.set(FID_PARAM, spillId);
    return params;
  }
  const enc = encode(state);
  if (enc.kind === 'inline' && enc.value) params.set(F_PARAM, enc.value);
  return params;
}

/** Read state from URLSearchParams. Caller must supply the spill resolver
 *  if it wants to resolve `fid` → state (typically via React Query). */
export function readFromParams(params: URLSearchParams): {
  state: f.FilterState;
  spillId: string | null;
} {
  const fid = params.get(FID_PARAM);
  if (fid) return { state: {}, spillId: fid };
  const value = params.get(F_PARAM) ?? '';
  return { state: value ? decode(value) : {}, spillId: null };
}

/* ──────────────────────────────────────────────────────────────────────────
 *  Internals
 * ────────────────────────────────────────────────────────────────────────── */

/** Stable JSON: keys sorted alphabetically at every level so two equivalent
 *  states encode to the same URL (cache-friendly, diff-friendly, share-friendly). */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined) continue;
    parts.push(`${JSON.stringify(k)}:${stableStringify(v)}`);
  }
  return `{${parts.join(',')}}`;
}

function b64urlEncode(s: string): string {
  // Both browsers and modern Node (v16+) expose btoa / atob globally.
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return globalThis.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64urlDecode(s: string): string {
  let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const bin = globalThis.atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
